"use client";

import { c as _c } from "react/compiler-runtime";
import type { MenuMegaTabNode } from "@multi-tenant-cms/types/menu";
import type { ElementNode } from "../types";
import type { Element } from "../ui/types";
import styles from "./mega-menu-header.module.css";
import { renderBuilderNode } from "./nodeRenderer";
import { normalizeLiveElementForRuntime } from "./normalizeLiveElementForRuntime";
type MegaTabRoot = MenuMegaTabNode;
function setPromoGradient(el: HTMLElement | null, gradient: string | undefined) {
  if (!el) {
    return;
  }
  if (gradient) {
    el.style.setProperty("--mmh-promo-gradient", gradient);
  } else {
    el.style.removeProperty("--mmh-promo-gradient");
  }
}
function groupsByColumn(groups: MegaTabRoot["groups"]): [typeof groups, typeof groups, typeof groups] {
  const cols: [typeof groups, typeof groups, typeof groups] = [[], [], []];
  for (const g of groups) {
    const i = Math.min(2, Math.max(0, g.columnIndex));
    cols[i].push(g);
  }
  return cols;
}

/**
 * Only absolute-ish units are trustworthy as an authored panel width — a
 * percentage/`auto`/`none` on the builder root would resolve against the
 * dropdown shell itself (which is exactly the value we're trying to compute),
 * so treat those as "not authored" rather than feeding a circular value back
 * into the shell's own sizing.
 */
function readAbsoluteCssLength(value: unknown): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }
  const trimmed = value.trim();
  return /^\d+(\.\d+)?(px|rem|em)$/.test(trimmed) ? trimmed : undefined;
}
type RootNodeLayoutShape = {
  contentWidth?: string;
  props?: {
    __builderContentWidth?: string;
  };
  style?: {
    layout?: {
      maxWidth?: {
        desktop?: string;
        tablet?: string;
        mobile?: string;
      };
      width?: {
        desktop?: string;
        tablet?: string;
        mobile?: string;
      };
    };
  };
  styles?: {
    maxWidth?: string;
    width?: string;
  };
};

/**
 * The mega menu builder root (a `container`/`columns` element authored in
 * the mega-menus Theme Builder canvas) carries its own width via either the
 * tokenized `style.layout.maxWidth` (persisted entry docs) or the flat
 * `styles.maxWidth` (live-editor elements) — same precedence `nodeRenderer`'s
 * `mergedInlineStyle` uses (token wins when both are present). Consumers use
 * this to size the panel *shell* around the authored width instead of
 * clamping it to an unrelated compact default.
 */
export function resolveMegaMenuBuilderPanelMaxWidth(root: MegaTabRoot): string | undefined {
  const rootNode = root.megaMenuContent?.root;
  if (!rootNode || typeof rootNode !== "object") {
    return undefined;
  }
  const node = rootNode as RootNodeLayoutShape;
  const tokenMaxWidth = node.style?.layout?.maxWidth;
  const tokenCandidate = readAbsoluteCssLength(tokenMaxWidth?.desktop ?? tokenMaxWidth?.tablet ?? tokenMaxWidth?.mobile);
  return tokenCandidate ?? readAbsoluteCssLength(node.styles?.maxWidth);
}

/** Same absolute-unit trust rule as `resolveMegaMenuBuilderPanelMaxWidth`, for `width` instead of `maxWidth`. */
function resolveAuthoredWidth(root: MegaTabRoot): string | undefined {
  const rootNode = root.megaMenuContent?.root;
  if (!rootNode || typeof rootNode !== "object") {
    return undefined;
  }
  const node = rootNode as RootNodeLayoutShape;
  const tokenWidth = node.style?.layout?.width;
  const tokenCandidate = readAbsoluteCssLength(tokenWidth?.desktop ?? tokenWidth?.tablet ?? tokenWidth?.mobile);
  return tokenCandidate ?? readAbsoluteCssLength(node.styles?.width);
}

/**
 * "Content width → Full viewport width (break out)" in the inspector
 * (`SharedLayoutThroughHtmlSections.tsx`) sets `contentWidth: "fullViewport"`
 * on the live `ContainerElement`/`ColumnsElement`. `toBuilderNode`
 * (`pageBuilderAdapter.ts`) serializes that as `props.__builderContentWidth`
 * on save — the field `nodeRenderer.tsx` actually reads at render time (via
 * `mergeFullBleedLayoutStyle`). A saved mega menu root may be in either shape
 * depending on whether it round-tripped through a save, so both are checked
 * directly (no need for the full `normalizeLiveElementForRuntime`/
 * `toBuilderNode` tree conversion just to read one flag off the root).
 */
function hasFullViewportContentWidth(root: MegaTabRoot): boolean {
  const rootNode = root.megaMenuContent?.root;
  if (!rootNode || typeof rootNode !== "object") {
    return false;
  }
  const node = rootNode as RootNodeLayoutShape;
  return node.contentWidth === "fullViewport" || node.props?.__builderContentWidth === "fullViewport";
}

/**
 * Sized directly off the viewport (12px safe inset per side, matching the
 * clamp inset `ClassicHeader.tsx` uses) — never a design cap. This also
 * needs `.dropdownBuilderCol`'s `container-type: inline-size` (see
 * `classic-header.module.css`) so the root's own `mergeFullBleedLayoutStyle`
 * 100cqw breakout resolves against *this* shell instead of an indefinite/
 * absent query container.
 */
const MEGA_MENU_PANEL_FULL_VIEWPORT_WIDTH_CSS = "calc(100vw - 24px)";

/**
 * No width authored at all: still needs a *definite* width (shrink-to-fit
 * would recompact — see the class doc comment in classic-header.module.css),
 * so default to the header's own content column width (`.barInner`'s
 * `max-width: 1240px`) rather than an arbitrary mega-menu-specific cap — an
 * unstyled panel then reads like the header's content width, not a compact
 * dropdown.
 */
const MEGA_MENU_PANEL_DEFAULT_WIDTH_CSS = "min(1240px, 94vw)";

/**
 * Resolves the Classic Header builder-backed dropdown shell width from the
 * mega menu root's own authored settings — Mega Menu Builder is the source
 * of truth, Classic Header only supplies the shell. Precedence:
 *
 *  1. Authored "Full viewport width" intent → near-full-viewport, uncapped
 *     by any design width.
 *  2. An authored absolute `maxWidth`, else absolute `width` (px/rem/em only)
 *     → that value, viewport-capped.
 *  3. Nothing authored → the header's own content width as a definite
 *     default (not a mega-menu-specific magic number).
 *
 * Always returns a definite (non-`auto`) CSS width expression.
 */
export function resolveMegaMenuBuilderPanelWidth(root: MegaTabRoot): string {
  if (hasFullViewportContentWidth(root)) {
    return MEGA_MENU_PANEL_FULL_VIEWPORT_WIDTH_CSS;
  }
  const authored = resolveMegaMenuBuilderPanelMaxWidth(root) ?? resolveAuthoredWidth(root);
  if (authored) {
    return `min(${authored}, 94vw)`;
  }
  return MEGA_MENU_PANEL_DEFAULT_WIDTH_CSS;
}

/** Renders saved mega menu page-builder content inside mega panel chrome. */
export function MegaMenuBuilderPanel(t0) {
  const $ = _c(8);
  const {
    root,
    columnClassName,
    gridClassName
  } = t0;
  const content = root.megaMenuContent;
  const rootNode = content?.root;
  if (!rootNode || typeof rootNode !== "object") {
    return null;
  }
  const t1 = `${gridClassName ?? styles.megaInner} ${styles.megaBuilderPanel}`;
  const t2 = columnClassName ?? styles.col;
  let t3;
  if ($[0] !== rootNode) {
    t3 = renderBuilderNode(normalizeLiveElementForRuntime(rootNode as Element | ElementNode), {});
    $[0] = rootNode;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== t2 || $[3] !== t3) {
    t4 = <div className={t2}>{t3}</div>;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t1 || $[6] !== t4) {
    t5 = <div className={t1}>{t4}</div>;
    $[5] = t1;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
}
export function MegaPanelColumns(t0) {
  const $ = _c(18);
  const {
    root,
    promo,
    columnClassName,
    gridClassName
  } = t0;
  if (root.megaMenuContent?.root) {
    let t1;
    if ($[0] !== columnClassName || $[1] !== gridClassName || $[2] !== root) {
      t1 = <MegaMenuBuilderPanel columnClassName={columnClassName} gridClassName={gridClassName} root={root} />;
      $[0] = columnClassName;
      $[1] = gridClassName;
      $[2] = root;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    return t1;
  }
  const t1 = gridClassName ?? styles.megaInner;
  let t2;
  if ($[4] !== columnClassName || $[5] !== root.groups || $[6] !== root.id) {
    let t3;
    if ($[8] !== columnClassName || $[9] !== root.id) {
      t3 = (colGroups, colIndex) => <div className={columnClassName ?? styles.col} key={`${root.id}-col-${colIndex}`}>{colGroups.map((group, groupIndex) => <div className={styles.group} key={group.id || `${root.id}-group-${groupIndex}`}><h4 className={styles.groupTitle}>{group.label}</h4><ul className={styles.linkList}>{group.links.map((link, linkIndex) => <li className={styles.linkItem} key={link.id || `${group.id || groupIndex}-link-${linkIndex}`}><a className={styles.link} href={link.href} rel={link.openInNewTab ? "noopener noreferrer" : undefined} target={link.openInNewTab ? "_blank" : undefined}><span className={styles.linkText}>{link.label}</span><span aria-hidden={true} className={styles.linkArrow}>→</span></a></li>)}</ul></div>)}</div>;
      $[8] = columnClassName;
      $[9] = root.id;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    t2 = groupsByColumn(root.groups).map(t3);
    $[4] = columnClassName;
    $[5] = root.groups;
    $[6] = root.id;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[11] !== columnClassName || $[12] !== promo) {
    t3 = promo?.enabled !== false && promo ? <a className={`${styles.promo} ${columnClassName ?? styles.col}`} href={promo.href || "#"} ref={el => setPromoGradient(el, promo.gradient)}><div aria-hidden={true} className={styles.promoBg} /><span className={styles.promoTag}>{promo.tag}</span><div><h3 className={styles.promoTitle}>{promo.title}</h3><div className={styles.promoCta}><span>Learn more</span><span aria-hidden={true}>→</span></div></div></a> : null;
    $[11] = columnClassName;
    $[12] = promo;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== t1 || $[15] !== t2 || $[16] !== t3) {
    t4 = <div className={t1}>{t2}{t3}</div>;
    $[14] = t1;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  return t4;
}